<template>
    <s-modal v-if="showModal" @mounted="onModalMounted" @close="reject" no-body-padding :size="modalWidth">
        <h3 slot="header">
            {{$t("adjust_image")}}
        </h3>
        <div slot="body">
            <div ref="croppieWrapper" @update="croppieZoomUpdate"></div>
        </div>
        <div slot="footer" class="custom-modal-footer">
            <div class="left">
                <button type="button" class="btn btn-secondary" :title="$t('rotate_90')" @click="rotate(90)">
                    <font-awesome-icon :icon="['fal', 'undo']"/>
                </button>
                <button type="button" class="btn btn-secondary" :title="$t('rotate_90_invert')" @click="rotate(-90)">
                    <font-awesome-icon :icon="['fal', 'redo']"/>
                </button>
            </div>
            <div class="center">
                <div class="zoom-slider-wrapper">
                    <input ref="zoomSlider" class="zoom-slider" type="range" aria-label="zoom" @input="zoomSliderChange" @change="zoomSliderChange">
                </div>
            </div>
            <div class="right">
                <button type="button" class="btn btn-primary" @click="resolve" v-focus>
                    {{$t("ok_btn")}}
                </button>
            </div>
        </div>
    </s-modal>
</template>

<script>
    import Croppie from "croppie";
    import SModal from "./SModal";

    export default {
        name: "SCropImage",
        i18nOptions: {namespaces: ['input_image']},
        components: {SModal},
        data: () => ({
            modalWidth: 800,
            showModal: false,
            imageSource: null,
            croppie: null,
            cropResolve: null,
            cropReject: null,
            resizeDelayTimeout: null,
            defaultConfig: {
                width: 500,
                height: 500,
                format: "jpeg",
                quality: 0.75,
                thumbs: []
            },
            config: {}
        }),
        beforeDestroy() {
            this.closeModal();
        },
        methods: {
            crop(imageSource, config) {
                return new Promise((resolve, reject) => {
                    for (let key in this.defaultConfig) {
                        if (typeof config[key] !== "undefined") {
                            this.config[key] = config[key]
                        } else {
                            this.config[key] = this.defaultConfig[key]
                        }
                    }

                    this.imageSource = imageSource
                    this.cropResolve = resolve
                    this.cropReject = reject
                    this.showModal = true
                })
            },
            onModalMounted() {
                this.initCroppie()
                window.addEventListener('resize', this.windowResize)
            },
            closeModal() {
                window.removeEventListener('resize', this.windowResize)

                this.imageSource = null
                this.cropResolve = null
                this.cropReject = null
                if (this.croppie) {
                    this.croppie.destroy()
                    this.croppie = null
                }
                this.showModal = false
            },
            reject() {
                if (this.cropReject) {
                    this.cropReject()
                }
                this.closeModal()
            },
            async resolve() {
                const result = []
                const imageResult = await this.croppie.result({
                    type: 'base64',
                    size: {width: this.config.width, height: this.config.height},
                    format: this.config.format,
                    quality: this.config.quality,
                })
                result.push(imageResult)
                if (this.config.thumbs.length) {
                    const thumbResults = await Promise.all(this.config.thumbs.map(thumb => this.croppie.result({
                        type: 'base64',
                        size: thumb,
                        format: this.config.format,
                        quality: this.config.quality,
                    })))
                    result.push(...thumbResults)
                }
                if (this.cropResolve) {
                    this.cropResolve(result)
                }
                this.closeModal()
            },
            rotate(degrees = 90) {
                if (this.croppie) {
                    this.croppie.rotate(degrees)
                    this.cloneZoomerParams()
                    this.croppie.elements.overlay.dispatchEvent(new Event('mousedown'))
                    window.dispatchEvent(new Event('mouseup'))
                    this.croppie.setZoom(this.$refs.zoomSlider.value)
                }
            },
            windowResize() {
                if (this.showModal && this.croppie && this.$refs.croppieWrapper.offsetWidth < this.modalWidth) {
                    if (this.resizeDelayTimeout) {
                        clearTimeout(this.resizeDelayTimeout)
                    }
                    this.resizeDelayTimeout = setTimeout(() => {
                        if (this.showModal && this.croppie) {
                            this.initCroppie()
                        }
                        this.resizeDelayTimeout = null
                    }, 200)
                }
            },
            initCroppie() {
                if (this.croppie) {
                    this.croppie.destroy()
                }

                const viewport = { width: this.config.width, height: this.config.height };
                let viewportRatio = 1;
                if (this.$refs.croppieWrapper.offsetWidth * 0.9 < viewport.width) {
                    viewportRatio = this.$refs.croppieWrapper.offsetWidth * 0.9 / viewport.width;
                }
                this.croppie = new Croppie(this.$refs.croppieWrapper, {
                    boundary: { height: (this.config.height + 80) * viewportRatio },
                    viewport: { width: this.config.width * viewportRatio, height: this.config.height * viewportRatio },
                    enableZoom: true,
                    mouseWheelZoom: true,
                    showZoomer: true,
                    enableOrientation: true,
                });
                this.croppie.bind({
                    url: this.imageSource,
                }, () => {
                    this.cloneZoomerParams()
                })
            },
            zoomSliderChange() {
                this.croppie.elements.zoomer.value = this.$refs.zoomSlider.value
                this.croppie.elements.zoomer.dispatchEvent(new Event('change'))
            },
            croppieZoomUpdate() {
                this.$refs.zoomSlider.value = this.croppie.elements.zoomer.value
            },
            cloneZoomerParams() {
                this.$refs.zoomSlider.step = this.croppie.elements.zoomer.step
                this.$refs.zoomSlider.min = this.croppie.elements.zoomer.min
                this.$refs.zoomSlider.max = this.croppie.elements.zoomer.max
                this.$refs.zoomSlider.value = this.croppie.elements.zoomer.value
            }
        }
    }
</script>

<style>
    .cr-slider-wrap {
        display: none;
    }
</style>

<style scoped>
    div.custom-modal-footer {
        width: 100%;
        justify-content: space-between;
        /*flex-wrap: nowrap;*/
    }

    .center {
        display: flex;
        flex-grow: 1;
        justify-content: center;
    }

    .zoom-slider-wrapper {
        display: flex;
        margin-top: 5px;
        margin-left: 5px;
        text-align: center;
        flex-basis: 400px;
        max-width: 80%;
    }

    /*************************************/
    /***** STYLING RANGE INPUT ***********/
    /*************************************/
    /*http://brennaobrien.com/blog/2014/05/style-input-type-range-in-every-browser.html */
    /*************************************/

    .zoom-slider {
        -webkit-appearance: none;
        /*removes default webkit styles*/
        /*border: 1px solid white; *//*fix for FF unable to apply focus style bug */
        width: 100%;
        /*required for proper track sizing in FF*/
        max-width: 100%;
        padding-top: 8px;
        padding-bottom: 8px;
        background-color: transparent;
    }

    .zoom-slider::-webkit-slider-runnable-track {
        width: 100%;
        height: 3px;
        background: rgba(0, 0, 0, 0.5);
        border: 0;
        border-radius: 3px;
    }

    .zoom-slider::-webkit-slider-thumb {
        -webkit-appearance: none;
        border: none;
        height: 16px;
        width: 16px;
        border-radius: 50%;
        background: #f96e46;
        margin-top: -6px;
    }

    .zoom-slider:focus {
        outline: none;
    }
    /*
    .zoom-slider:focus::-webkit-slider-runnable-track {
    background: #ccc;
    }
    */

    .zoom-slider::-moz-range-track {
        width: 100%;
        height: 3px;
        background: rgba(0, 0, 0, 0.5);
        border: 0;
        border-radius: 3px;
    }

    .zoom-slider::-moz-range-thumb {
        border: none;
        height: 16px;
        width: 16px;
        border-radius: 50%;
        background: #ddd;
        margin-top: -6px;
    }

    /*hide the outline behind the border*/
    .zoom-slider:-moz-focusring {
        outline: 1px solid white;
        outline-offset: -1px;
    }

    .zoom-slider::-ms-track {
        width: 100%;
        height: 5px;
        background: transparent;
        /*remove bg colour from the track, we'll use ms-fill-lower and ms-fill-upper instead */
        border-color: transparent;/*leave room for the larger thumb to overflow with a transparent border */
        border-width: 6px 0;
        color: transparent;/*remove default tick marks*/
    }
    .zoom-slider::-ms-fill-lower {
        background: rgba(0, 0, 0, 0.5);
        border-radius: 10px;
    }
    .zoom-slider::-ms-fill-upper {
        background: rgba(0, 0, 0, 0.5);
        border-radius: 10px;
    }
    .zoom-slider::-ms-thumb {
        border: none;
        height: 16px;
        width: 16px;
        border-radius: 50%;
        background: #ddd;
        margin-top:1px;
    }
    .zoom-slider:focus::-ms-fill-lower {
        background: rgba(0, 0, 0, 0.5);
    }
    .zoom-slider:focus::-ms-fill-upper {
        background: rgba(0, 0, 0, 0.5);
    }
    /*******************************************/
</style>